/*---------------------------------------------------------------------------------------------
 *  Copyright (c) by Solusek Systems. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { Link } from "react-router-dom";
import Auth from "../../shared/Auth";
import { qreq } from "../../shared/qrequest";
import BaseCom from "../BaseCom";
import Icon from "../Icon";
import { withParamsAndNavigate } from "../WithParamsAndNavigate";
import Card from "../Card";
import FormLabel from "../FormLabel";
import PurchaseOrders from "../PurchaseOrders";
import FormInput from "../FormInput";
import Alert from "../Alert";
import Address from "../Address";
import AppActions from "../AppActions";
import L from "../Lang";
import { SidebarGlobal } from "../_Sidebar";
import DataTable from "../DataTable";
import { Pop } from "../PopModal";
import Orders from "../Orders";
import Leads from "../Leads";
import AccountPatientChart from "../chart/AccountPatientChart";

class AccountView extends BaseCom {

    constructor(props) {
        super(props);
        this.state = {
            newUser: {}
        };
    }

    async componentDidMount() {
        await this.getSettings();
        await this.load();
        await this.loadRel();
        await this.loadAccounts();
        const u = await Auth.getLoginAsync();
        if (u) {
            if (Auth.hasPolicy('admin')) {
                await this.loadUsers();
                await this.loadRoles();
            }
        }
        SidebarGlobal.setSubNav(<>
            <Link to={'/account/' + this.props.params.id + '/edit'} className="btn btn-primary"><Icon icon="faPencil" /> <L>Edit</L></Link>
        </>);
    }

    async componentDidUpdate(prevProps) {
        if (this.props.params.id !== prevProps.params.id) {
            await this.load();
            await this.loadRel();
            await this.loadAccounts();
            const u = await Auth.getLoginAsync();
            if (u) {
                if (Auth.hasPolicy('admin')) {
                    await this.loadUsers();
                    await this.loadRoles();
                }
            }
        }
    }

    componentWillUnmount() {
        SidebarGlobal.clearSubNav();
    }

    load = async () => {
        try {
            let j = await qreq.getAsync(`/api/account/get/${this.props.params.id}`);
            if (j.item)
                this.setState({ item: j.item });
            else
                this.setState({ error: j.errorMessage });
        }
        catch (e) {
            this.unkownErrorCallback(e);
        }
    }

    loadRel = async () => {
        try {
            let j = await qreq.getAsync(`/api/account/rel/${this.props.params.id}`);
            if (j.list)
                this.setState({ rel: j.list });
            else {
                this.setState({ rel: [] });
            }
        }
        catch (e) {
            this.unkownErrorCallback(e);
        }
    }

    loadAccounts = async () => {
        try {
            await this.fillListAsync('/api/account/list', 'accounts');
        }
        catch { }
    }

    loadUsers = async () => {
        try {
            await this.fillListAsync('/api/account/' + this.props.params.id + '/user/list', 'userList');
        }
        catch { }
    }

    loadRoles = async () => {
        try {
            await this.fillListAsync('/api/role/list', 'roleList');
        }
        catch { }
    }

    highlightSelf = (txt, id) => {
        if (!txt)
            return <i><L>None</L></i>;
        if (txt === this.state.item?.name)
            return <strong className="bg-warning p-1 rounded">{txt}</strong>;
        return <Link title="View Account" to={`/account/${id}`}>{txt}</Link>;
    }

    showAddRel = () => {
        this.setState({ rel: {}, showAddRel: true });
    }

    changeRel = () => {
        this.setState({ rel: { ...this.state.rel } });
    }

    submitRel = async (e) => {
        e?.preventDefault();
        try {
            let info = { ...this.state.rel };
            let j = await qreq.postAsync('/api/account/rel/save', info);
            if (j.errorCode === 0) {
                this.loadRel();
                this.setState({ showAddRel: false });
            }
            else
                this.alert(j.errorMessage);
        }
        catch (e) {
            this.unkownErrorCallback(e);
        }
    }

    change = () => {
        this.setState({
            newUser: { ...this.state.newUser }
        });
    }

    submitUser = async (e) => {
        e?.preventDefault();
        const info = { ...this.state.newUser };
        info.primaryAccountID = this.state.item.id;
        if (info.isBilling) {
            const role = this.state.roleList.find(x => x._Roles === 'distribution-recipient,distributor,billing-distributor,consumer,user');
            if (!role) {
                Pop.show(<L>Role for billing not found.</L>, <L>Error</L>);
                return;
            }
            info.roleGroupID = role.id;
        }
        else {
            const role = this.state.roleList.find(x => x._Roles === 'distributor,sub-distributor,consumer,user,distribution-recipient');
            if (!role) {
                Pop.show(<L>Role for distributor not found.</L>, <L>Error</L>);
                return;
            }
            info.roleGroupID = role.id;
        }

        info.resendInvite = true;

        this.setState({ submitting: true });
        try {
            const j = await qreq.postAsync('/api/user/save', info);
            if (j.errorCode === 0) {
                this.success('Saved!');
                this.setState({ showAddUser: false });
                this.loadUsers();
            }
            else
                this.alert(j.errorMessage);
        }
        catch (e) {
            this.unkownErrorCallback(e);
        }
        this.setState({ submitting: false });
    }

    render() {
        return (
            <div className="container-fluid">
                <h1>{this.state.error ? 'Account' : this.state.item?.name} {(Auth.hasRole('account-manager') || Auth.hasRole('distributor'))
                    && !this.state.error
                    && <Link to="edit" title="Edit Account" role="button">
                        <Icon icon="faPencil" />
                    </Link>}</h1>

                <AppActions>
                    <Link to="edit" className="btn btn-primary btn-lg"><Icon icon="faPencil" /> <L>Edit</L></Link>
                </AppActions>

                {!this.state.error && <>

                    <div className="row">
                        <div className="col-md-6">
                            <Card title="Account Details">
                                <FormLabel label="Name" model={this.state.item?.name} />
                                <FormLabel label="Type" model={this.state.item?.accountTypeName} />
                            </Card>
                        </div>
                        {this.state.item?.address && this.state.item?.address.line1 && <div className="col-md-6">
                            <Card title="Contact Address">
                                <Address readOnly condensed item={this.state.item?.address} />
                            </Card>
                        </div>}
                        {this.state.item?.billingAddress && this.state.item?.billingAddress.line1 && <div className="col-md-6">
                            <Card title="Billing Address">
                                <Address readOnly condensed item={this.state.item?.billingAddress} />
                            </Card>
                        </div>}
                        {this.state.item?.shippingAddress && this.state.item?.shippingAddress.line1 && <div className="col-md-6">
                            <Card title="Shipping Address">
                                <Address readOnly condensed item={this.state.item?.shippingAddress} />
                            </Card>
                        </div>}
                        {this.state.MODEL === 'WMS' && <div className="col-md-6">
                            <Card title="Patient Approval">
                                {this.state.item?.id && <AccountPatientChart accountID={this.state.item.id} />}
                            </Card>
                        </div>}
                    </div>
                </>}
                {this.state.error && <Alert type="warning" message={this.state.error} />}
                {this.state.MODEL === 'MNF' && Auth.hasRole('admin') && <div className="row">
                    {/* RELATIONS */}
                    <div className="col-md">
                        <Card title="Relations">
                            {this.state.rel?.length > 0 && <table className="table table-bordered">
                                <thead>
                                    <tr>
                                        <th><L>Billing</L></th>
                                        <th><L>Distributor</L></th>
                                        <th><L>Sub Distributor</L></th>
                                        <th><L>Shipping</L></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {this.state.rel?.map((i, idx) => <tr key={idx}>
                                        <td>{this.highlightSelf(i.billingAccountName, i.billingAccountID)}</td>
                                        <td>{this.highlightSelf(i.distributorAccountName, i.distributorAccountID)}</td>
                                        <td>{this.highlightSelf(i.subDistributorAccountName, i.subDistributorAccountID)}</td>
                                        <td>{this.highlightSelf(i.shippingAccountName, i.shippingAccountID)}</td>
                                    </tr>)}
                                </tbody>
                            </table>}
                            {this.state.rel?.length === 0 && <p><L>No relations found.</L></p>}
                            {Auth.hasRole('admin') && this.state.accounts && <div>

                                {!this.state.showAddRel
                                    && <button type="button" className="btn btn-primary" onClick={() => this.showAddRel()}>Add Relation</button>}
                                {this.state.showAddRel && <form onSubmit={this.submitRel}>
                                    <div className="row">
                                        <div className="col-md">
                                            <FormInput model={this.state.rel}
                                                name="billingAccountID"
                                                label="Billing Account"
                                                type="select"
                                                options={this.state.accounts.filter(a => a.level === 0)}
                                                onChange={this.changeRel}
                                            />
                                        </div>
                                        <div className="col-md">
                                            <FormInput model={this.state.rel}
                                                name="distributorAccountID"
                                                label="Distributor Account"
                                                type="select"
                                                options={this.state.accounts.filter(a => a.level > 0)}
                                                onChange={this.changeRel}
                                            />
                                        </div>
                                        <div className="col-md">
                                            <FormInput model={this.state.rel}
                                                name="subDistributorAccountID"
                                                label="Sub Distributor Account"
                                                type="select"
                                                options={this.state.accounts.filter(a => a.level > 0)}
                                                onChange={this.changeRel}
                                            />
                                        </div>
                                        <div className="col-md">
                                            <FormInput model={this.state.rel}
                                                name="shippingAccountID"
                                                label="Shipping Account"
                                                type="select"
                                                options={this.state.accounts.filter(a => a.level === 3)}
                                                onChange={this.changeRel}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md">
                                        <button type="submit" className="btn btn-primary" onClick={this.saveRel}><L>Save</L></button>
                                        <button type="button" className="btn btn-secondary" onClick={() => this.setState({ rel: {}, showAddRel: false })}>Cancel</button>
                                    </div>
                                </form>}
                            </div>}
                        </Card>
                    </div>
                    {/* USERS */}
                    {Auth.hasPolicy('admin') && <div className="col-md">
                        <Card title="Users">
                            <DataTable columns={[
                                {
                                    name: 'fullName', title: 'Name', valueFunc: row => <Link to={'/user-profile/' + row.id}>
                                        <Icon icon="faUser" /> {row.fullName}
                                    </Link>
                                },
                                { name: 'roleGroupName', title: 'Role' }
                            ]} data={this.state.userList || []} />
                            <div>
                                {!this.state.showAddUser && <button type="button" className="btn btn-primary" onClick={() => this.setState({ showAddUser: true })}>
                                    <Icon icon="faPlus" /> <L>Add User</L>
                                </button>}
                                {this.state.showAddUser && <form onSubmit={this.submitUser} className="bg-light p-2 rounded">
                                    <h4>New User</h4>
                                    <div className="row">
                                        <div className="col-md-6">
                                            <FormInput model={this.state.newUser} name="firstName" label="First Name" required onChange={this.change} labelAsPlaceholder />
                                        </div>
                                        <div className="col-md-6">
                                            <FormInput model={this.state.newUser} name="lastName" label="Last Name" required onChange={this.change} labelAsPlaceholder />
                                        </div>
                                        <div className="col-md-6">
                                            <FormInput model={this.state.newUser} name="email" label="Email" required onChange={this.change} labelAsPlaceholder />
                                        </div>
                                        <div className="col-md-6">
                                            <FormInput model={this.state.newUser} name="roleGroupID" label="Role" type="select" required options={this.state.roleList?.filter(x => x.isLinkedToAccounts) || []} onChange={this.change} labelAsPlaceholder />
                                        </div>
                                    </div>
                                    <Alert>
                                        <L>Creating a user here will send them an invite to their email.</L>
                                    </Alert>
                                    <div className="d-flex justify-content-end gap-1">
                                        <button type="submit" className="btn btn-success" disabled={this.state.submitting}>
                                            <Icon icon="faSave" /> <L>Submit</L>
                                        </button>
                                        <button type="button" className="btn btn-secondary" onClick={() => this.setState({ showAddUser: false })} disabled={this.state.submitting}>
                                            <Icon icon="faCancel" /> <L>Cancel</L>
                                        </button>
                                    </div>
                                </form>}
                            </div>
                        </Card>
                    </div>}
                </div>}
                {this.state.MODEL === 'MNF' && Auth.hasRole('admin') && this.state.item && <Card title="Purchase Orders">
                    <PurchaseOrders com accountID={this.props.params.id} />
                </Card>}
                {this.state.MODEL === 'WMS' && Auth.hasRole('manager') && this.state.item && <Card title="Patients" id="patient">
                    <Orders com accountID={this.props.params.id} />
                </Card>}
                {this.state.MODEL === 'WMS' && Auth.hasRole('manager') && this.state.item && <Card title="Queue" id="queue">
                    <Leads com referringAccountID={this.props.params.id} />
                </Card>}
            </div>
        );
    }
}

export default withParamsAndNavigate(AccountView);